import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { closeHTMLDrawer } from "../../state/slices/htmlDrawerSlice";

export const HTMLDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, htmlContent, reportName, loading, error } = useAppSelector(
    (state) => state.htmlDrawer
  );
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    dispatch(closeHTMLDrawer());
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Wrapper class so injected content can grow and the drawer scroll container works.
  // Some reports (e.g. Invoice_info, purchase_pdf_view) use height:100% on body/root,
  // which prevents the drawer from scrolling; job card summary does not. Wrapping
  // in a div with min-height: min-content ensures the scroll happens in the drawer.
  const INJECTED_WRAPPER_CLASS = "html-drawer-injected-wrapper";

  // Inject HTML content into div
  useEffect(() => {
    if (isOpen && htmlContent && contentRef.current) {
      // Clear previous content
      contentRef.current.innerHTML = "";

      let contentToInject: string;

      if (htmlContent.includes("<!DOCTYPE") || htmlContent.includes("<html")) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        const headStyles = doc.querySelectorAll("head style");
        let styleContent = "";
        headStyles.forEach((style) => {
          styleContent += style.outerHTML;
        });
        const bodyContent = doc.body ? doc.body.innerHTML : htmlContent;
        contentToInject = styleContent + bodyContent;
      } else {
        contentToInject = htmlContent;
      }

      // Wrap so content can grow and drawer scrolls. Override report root height/overflow
      // that would otherwise prevent scrolling (e.g. body { height: 100%; overflow: hidden }).
      const scrollFixStyle =
        `<style>.${INJECTED_WRAPPER_CLASS}, .${INJECTED_WRAPPER_CLASS} * { box-sizing: border-box; } ` +
        `.${INJECTED_WRAPPER_CLASS} > * { height: auto !important; min-height: auto !important; max-height: none !important; overflow: visible !important; }</style>`;
      contentRef.current.innerHTML =
        scrollFixStyle +
        `<div class="${INJECTED_WRAPPER_CLASS}" style="display: block; min-height: 0;">${contentToInject}</div>`;
    }
  }, [isOpen, htmlContent]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 !z-[100050] bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer - fixed height so scroll area has a definite size */}
      <div
        className="fixed right-0 top-0 bottom-0 !z-[100051] w-full max-w-5xl bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col"
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          height: "100vh",
          maxHeight: "100dvh",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            {/* <svg
              className="w-6 h-6 text-primary-600 dark:text-primary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg> */}
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {""}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
            aria-label="Close drawer"
          >
            <svg
              className="w-6 h-6 text-gray-600 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content area - explicit height so scroll always works (no flex dependency) */}
        <div
          className="relative flex-1 min-h-0"
          style={{ height: "calc(100vh - 4.5rem)", minHeight: 0 }}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-600 dark:text-gray-300">Loading content...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800 p-6 overflow-auto">
              <div className="text-center">
                <svg
                  className="w-16 h-16 text-red-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  Error Loading Content
                </p>
                <p className="text-gray-600 dark:text-gray-400">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && htmlContent && (
            <div
              ref={contentRef}
              className="html-drawer-content-wrapper h-full overflow-auto"
              style={{
                boxSizing: "border-box",
                backgroundColor: "#ffffff",
                padding: "20px",
                isolation: "isolate",
                colorScheme: "light",
                WebkitOverflowScrolling: "touch",
              }}
            />
          )}

          {!loading && !error && !htmlContent && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800">
              <p className="text-gray-500 dark:text-gray-400">No content to display</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
